import AbstractApiService from '../abstractApiService';
import { api } from 'pages/APIs';
import { getLokiApiUrl } from 'src/utils/lokiUrl';

export default class SystemLogsApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  public async getLogs(data: any): Promise<any> {
    return super.get(api.get_system_logs + '?' + data);
  }

  public async getLogStats(data: any): Promise<any> {
    return super.get(api.get_system_logs_count + '?' + data);
  }

  public async getLogCounts(
    baseQuery: string,
    startTime: string,
    endTime: string,
    orgId?: string,
  ): Promise<any> {
    let queryWithOrgId = baseQuery;
    if (orgId) {
      queryWithOrgId = `${baseQuery} | json | org_id="${orgId}"`;
    }

    const queries = {
      total: `max(sum(count_over_time(${queryWithOrgId}[7d])))`,
      errors: `max(sum(count_over_time(${queryWithOrgId} |~ "ERROR|Exception|Failed"[7d])))`,
      warnings: `max(sum(count_over_time(${queryWithOrgId} |~ "WARN|WARNING"[7d])))`,
      info: `max(sum(count_over_time(${queryWithOrgId} |~ "INFO"[7d])))`,
      debug: `max(sum(count_over_time(${queryWithOrgId} |~ "DEBUG"[7d])))`,
    };

    try {
      const queryEntries = Object.entries(queries);

      const results = await Promise.all(
        queryEntries.map(async ([type, query]) => {
          try {
            return await this.executeLokiQuery(query, startTime, endTime);
          } catch (error) {
            console.error(
              `[SystemLogsApiService] Failed ${type} query:`,
              error,
            );
            return { data: { result: [] } };
          }
        }),
      );

      return {
        total: this.sumLokiMetricResult(results[0]),
        errors: this.sumLokiMetricResult(results[1]),
        warnings: this.sumLokiMetricResult(results[2]),
        info: this.sumLokiMetricResult(results[3]),
        debug: this.sumLokiMetricResult(results[4]),
      };
    } catch (error) {
      console.error('[SystemLogsApiService] Error in getLogCounts:', error);
      throw error;
    }
  }

  private async executeLokiQuery(
    query: string,
    start: string,
    end: string,
  ): Promise<any> {
    const params = new URLSearchParams();
    params.append('query', query);
    params.append('time', end);

    const queryEndpoint = getLokiApiUrl() + '/query';
    const fullUrl = queryEndpoint + '?' + params.toString();

    try {
      return await this.directLokiCall(fullUrl);
    } catch (error) {
      console.error(
        '[SystemLogsApiService] Query Failed for:',
        query.substring(0, 50) + '...',
        error,
      );
      throw error;
    }
  }

  private async directLokiCall(url: string): Promise<any> {
    return this.getWithoutOrgId(url);
  }

  private sumLokiMetricResult(result: any): number {
    if (result?.data?.result?.length > 0) {
      const firstResult = result.data.result[0];

      if (firstResult?.value?.length >= 2) {
        return parseFloat(firstResult.value[1] || '0');
      }

      if (firstResult?.values?.length > 0) {
        return firstResult.values.reduce((sum: number, value: any[]) => {
          return sum + parseFloat(value[1] || '0');
        }, 0);
      }
    }

    return 0;
  }

  public async getLogDetail(logId: string): Promise<any> {
    const params = new URLSearchParams();
    params.append('search', logId);
    params.append('page_size', '1');

    return super.get(api.get_system_logs + '?' + params.toString());
  }

  public async exportLogs(data: any): Promise<any> {
    return super.get(api.get_system_logs + '?' + data);
  }

  public async getErrorSummary(): Promise<any[]> {
    return super.get(api.get_system_logs_summary);
  }

  public async getSystemHealth(): Promise<any> {
    return super.get(api.get_system_health);
  }

  public async getComponentHealth(componentName: string): Promise<any> {
    return super.get(
      api.get_system_component_health.replace(':component_name', componentName),
    );
  }

  public async getSystemInfo(): Promise<any> {
    return super.get(api.get_system_info);
  }

  public async getSystemMetrics(): Promise<any> {
    return super.get(api.get_system_metrics);
  }

  public async getAvailableServices(): Promise<{ [key: string]: string }> {
    return Promise.resolve({
      'doc2-api': 'doc2-api',
      'extraction-service': 'extraction-service',
      'ocr-service': 'ocr-service',
      'workflow-service': 'workflow-service',
      'auto-accounting': 'auto-accounting',
      'import-email': 'import-email',
      'import-ftp': 'import-ftp',
      docflow: 'docflow',
      operator: 'operator',
      docnet: 'docnet',
    });
  }
}
